import { DUPE_API_URL } from '@dupe/common/constants'
import {
  buildQueuedSeoGeneratedArtifact,
  type SeoGeneratedComponents,
  type PublishedResearchReport,
  type ResearchReportStatus,
  type SeoGeneratedProductImages,
} from '@dupe/common/research-report'
import {
  type QueryClient,
  type QueryKey,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import useAuthUser, { type AuthUserWithToken } from '~/src/hooks/useAuthUser'
import fetcher from '~/src/lib/fetcher'
import type { SeoAdminComponentTarget } from '~/src/lib/admin/seoReportState'
import { deriveSeoAdminReportState } from '~/src/lib/admin/seoReportState'

export interface EligibleTask {
  taskId: string
  title: string
  status: string
  completedAt: string | null
  productsCount: number
  archetype: string | null
  productImages: string[] | null
}

interface CreateSeoReportResponse {
  data: PublishedResearchReport
}

interface SeoReportsListResponse {
  data: SeoReportListItem[]
  total: number | null
}

type SeoReportListItem = PublishedResearchReport & {
  documentPayload?: unknown
}

interface EligibleTasksResponse {
  data: EligibleTask[]
  total: number | null
  hasMore: boolean
}

interface SeoReportByTaskResponse {
  data: PublishedResearchReport | null
}

interface SeoReportByIdResponse {
  data: PublishedResearchReport
}

interface RegenerateSeoReportSlugResponse {
  data: PublishedResearchReport
  slugSource?: string
}

interface SeoReportProductImagesQueueResponse {
  message: string
  data: {
    queued?: boolean | number
    matched?: number
    skippedReady?: number
    skippedInFlight?: number
    results?: Array<{
      reportId: string
      slug: string
      status: 'queued' | 'skipped'
      reason?: string
      eventIds?: string[]
    }>
    eventIds?: string[]
    reason?: string
  }
}

type SeoReportCacheSnapshot = Array<[QueryKey, unknown]>
type SeoReportCacheMutationContext = { snapshot: SeoReportCacheSnapshot }
type PublishTaskForSeoRequestResult = {
  reportId: string
  slug: string
  created: boolean
  report: PublishedResearchReport
}

interface SeoReportGenerationCachePatch {
  componentTargets?: SeoAdminComponentTarget[]
  seoMetadata?: boolean
  productImages?: boolean
  reportProcessing?: boolean
  queuedAt?: string
}

const SEO_REPORT_ADMIN_QUERY_PREFIXES = [
  ['admin-seo-reports'],
  ['admin-seo-report-task'],
  ['admin-seo-report-id'],
] as const satisfies QueryKey[]

const SEO_REPORT_METADATA_TARGETS = ['seo_title', 'meta_description'] as const

function hasActiveGeneration(
  report: PublishedResearchReport | null | undefined
): boolean {
  if (!report) return false
  return deriveSeoAdminReportState(report).shouldPoll
}

function getQueuedComponentTargets(
  targets: readonly SeoAdminComponentTarget[]
): SeoAdminComponentTarget[] {
  const next = new Set(targets)
  if (next.has('image_check')) {
    next.add('hero_image')
  }
  return [...next]
}

function queueGeneratedComponentTargets(
  generatedComponents: SeoGeneratedComponents | null | undefined,
  targets: readonly SeoAdminComponentTarget[],
  queuedAt: string
): SeoGeneratedComponents {
  const next: SeoGeneratedComponents = { ...(generatedComponents ?? {}) }
  const componentRecords = next as Record<
    string,
    Record<string, unknown> | undefined
  >

  for (const target of getQueuedComponentTargets(targets)) {
    componentRecords[target] = buildQueuedSeoGeneratedArtifact({
      existing: componentRecords[target] ?? null,
      now: queuedAt,
    })
  }

  return next
}

function queueSeoMetadataTargets(
  generatedComponents: SeoGeneratedComponents | null | undefined,
  queuedAt: string
): SeoGeneratedComponents {
  const next: SeoGeneratedComponents = { ...(generatedComponents ?? {}) }
  const componentRecords = next as Record<
    string,
    Record<string, unknown> | undefined
  >

  for (const target of SEO_REPORT_METADATA_TARGETS) {
    componentRecords[target] = buildQueuedSeoGeneratedArtifact({
      existing: componentRecords[target] ?? null,
      now: queuedAt,
    })
  }

  return next
}

export function queueSeoReportGenerationForAdminCache(
  report: PublishedResearchReport,
  patch: SeoReportGenerationCachePatch
): PublishedResearchReport {
  const queuedAt = patch.queuedAt ?? new Date().toISOString()
  let generatedComponents = report.generatedComponents ?? null

  if (patch.componentTargets?.length) {
    generatedComponents = queueGeneratedComponentTargets(
      generatedComponents,
      patch.componentTargets,
      queuedAt
    )
  }

  if (patch.seoMetadata) {
    generatedComponents = queueSeoMetadataTargets(generatedComponents, queuedAt)
  }

  return {
    ...report,
    status: patch.reportProcessing ? 'queued' : report.status,
    generatedComponents,
    generatedProductImages: patch.productImages
      ? (buildQueuedSeoGeneratedArtifact({
          existing: report.generatedProductImages ?? {},
          now: queuedAt,
        }) as SeoGeneratedProductImages)
      : report.generatedProductImages,
    lastError: patch.reportProcessing ? null : report.lastError,
    updatedAt: queuedAt,
  }
}

function getSeoReportCacheSnapshot(
  queryClient: QueryClient
): SeoReportCacheSnapshot {
  const seen = new Set<string>()
  const snapshot: SeoReportCacheSnapshot = []

  for (const queryKey of SEO_REPORT_ADMIN_QUERY_PREFIXES) {
    for (const entry of queryClient.getQueriesData({
      queryKey,
      exact: false,
    })) {
      const key = JSON.stringify(entry[0])
      if (seen.has(key)) continue
      seen.add(key)
      snapshot.push(entry)
    }
  }

  return snapshot
}

async function cancelSeoReportCacheQueries(queryClient: QueryClient) {
  await Promise.all(
    SEO_REPORT_ADMIN_QUERY_PREFIXES.map((queryKey) =>
      queryClient.cancelQueries({ queryKey, exact: false })
    )
  )
}

function restoreSeoReportCacheSnapshot(
  queryClient: QueryClient,
  snapshot: SeoReportCacheSnapshot | undefined
) {
  if (!snapshot) return
  for (const [queryKey, data] of snapshot) {
    queryClient.setQueryData(queryKey, data)
  }
}

function patchSeoReportCache(
  queryClient: QueryClient,
  reportIds: readonly string[],
  updater: (report: PublishedResearchReport) => PublishedResearchReport
) {
  const ids = new Set(reportIds)
  if (ids.size === 0) return

  queryClient.setQueriesData<SeoReportListItem[]>(
    { queryKey: ['admin-seo-reports'], exact: false },
    (cachedReports) =>
      cachedReports?.map((report) =>
        ids.has(report.id) ? (updater(report) as SeoReportListItem) : report
      )
  )

  queryClient.setQueriesData<PublishedResearchReport | null>(
    { queryKey: ['admin-seo-report-task'], exact: false },
    (cachedReport) =>
      cachedReport && ids.has(cachedReport.id)
        ? updater(cachedReport)
        : cachedReport
  )

  for (const reportId of ids) {
    queryClient.setQueryData<PublishedResearchReport>(
      ['admin-seo-report-id', reportId],
      (cachedReport) =>
        cachedReport && ids.has(cachedReport.id)
          ? updater(cachedReport)
          : cachedReport
    )
  }
}

function getSeoReportListStatusFromQueryKey(queryKey: QueryKey) {
  const status = queryKey[1]
  return typeof status === 'string' ? status : 'all'
}

function shouldIncludeReportInListQuery(
  queryKey: QueryKey,
  report: PublishedResearchReport
) {
  const status = getSeoReportListStatusFromQueryKey(queryKey)
  return status === 'all' || status === report.status
}

function upsertSeoReportCache(
  queryClient: QueryClient,
  report: PublishedResearchReport
) {
  for (const [queryKey, cachedReports] of queryClient.getQueriesData<
    SeoReportListItem[]
  >({
    queryKey: ['admin-seo-reports'],
    exact: false,
  })) {
    if (!Array.isArray(cachedReports)) continue
    const shouldInclude = shouldIncludeReportInListQuery(queryKey, report)
    const existing = cachedReports.some((cached) => cached.id === report.id)
    const nextReports =
      existing && !shouldInclude
        ? cachedReports.filter((cached) => cached.id !== report.id)
        : existing
          ? cachedReports.map((cached) =>
              cached.id === report.id ? (report as SeoReportListItem) : cached
            )
          : shouldInclude
            ? [report as SeoReportListItem, ...cachedReports]
            : cachedReports
    queryClient.setQueryData(queryKey, nextReports)
  }

  if (report.sourceTaskId) {
    queryClient.setQueryData(
      ['admin-seo-report-task', report.sourceTaskId],
      report
    )
  }
  queryClient.setQueryData(['admin-seo-report-id', report.id], report)
}

function removeSeoReportFromAdminCache(
  queryClient: QueryClient,
  report: PublishedResearchReport
) {
  queryClient.setQueriesData<SeoReportListItem[]>(
    { queryKey: ['admin-seo-reports'], exact: false },
    (cachedReports) =>
      cachedReports?.filter((cached) => cached.id !== report.id)
  )

  if (report.sourceTaskId) {
    queryClient.setQueryData(
      ['admin-seo-report-task', report.sourceTaskId],
      null
    )
  }
  queryClient.setQueryData(['admin-seo-report-id', report.id], null)
}

function isFetchConflict(error: unknown): boolean {
  if (!(error instanceof Error)) return false
  const fetchError = error as Error & {
    code?: string
    status?: number
  }
  return fetchError.status === 409 && fetchError.code === 'Conflict'
}

function hasHttpStatus(error: unknown): boolean {
  return (
    error instanceof Error &&
    typeof (error as Error & { status?: unknown }).status === 'number'
  )
}

async function rollbackCreatedSeoReportAfterPublishFailure(input: {
  report: PublishedResearchReport
  authUser?: AuthUserWithToken | null | undefined
  publishError: unknown
}) {
  if (!hasHttpStatus(input.publishError)) {
    return false
  }

  await fetcher<{ message: string }>(
    `${DUPE_API_URL}/api/admin/seo-reports/${input.report.id}`,
    {
      method: 'DELETE',
      authUser: input.authUser,
    }
  )
  return true
}

export async function publishTaskForSeoRequest(input: {
  taskId: string
  title: string
  authUser?: AuthUserWithToken | null | undefined
  onReportReadyForPublish?: (
    report: PublishedResearchReport,
    context: { created: boolean }
  ) => void
  onCreatedReportRollback?: (report: PublishedResearchReport) => void
}): Promise<PublishTaskForSeoRequestResult> {
  let report: PublishedResearchReport
  let created = false

  try {
    const response = await fetcher<CreateSeoReportResponse>(
      `${DUPE_API_URL}/api/admin/seo-reports`,
      {
        method: 'POST',
        authUser: input.authUser,
        dataOrParams: {
          taskId: input.taskId,
          keyword: input.title,
        },
      }
    )
    report = response.data
    created = true
  } catch (error) {
    if (!isFetchConflict(error)) {
      throw error
    }

    const existing = await fetcher<SeoReportByTaskResponse>(
      `${DUPE_API_URL}/api/admin/seo-reports/task/${encodeURIComponent(input.taskId)}`,
      {
        method: 'GET',
        authUser: input.authUser,
      }
    )
    if (!existing.data) {
      throw error
    }
    report = existing.data
  }

  input.onReportReadyForPublish?.(report, { created })

  try {
    await fetcher<{ message: string }>(
      `${DUPE_API_URL}/api/admin/seo-reports/${report.id}/publish`,
      {
        method: 'POST',
        authUser: input.authUser,
      }
    )
  } catch (error) {
    if (created) {
      const rolledBack = await rollbackCreatedSeoReportAfterPublishFailure({
        report,
        authUser: input.authUser,
        publishError: error,
      }).catch(() => false)
      if (rolledBack) {
        input.onCreatedReportRollback?.(report)
      }
    }
    throw error
  }

  return {
    reportId: report.id,
    slug: report.slug,
    created,
    report,
  }
}

function hasAnyActiveGeneration(
  reports: SeoReportListItem[] | undefined
): boolean {
  if (!reports) return false
  return reports.some((report) => hasActiveGeneration(report))
}

export const LIVE_SEO_REPORTS_REFETCH_INTERVAL_MS = 15000
export const LIVE_SEO_REPORT_DETAIL_REFETCH_INTERVAL_MS = 3000
export const SEO_REPORTS_ADMIN_PAGE_SIZE = 200
const SEO_REPORTS_ADMIN_MAX_PAGES = 10

export function getSeoReportsRefetchInterval(args: {
  live?: boolean
  reports: SeoReportListItem[] | undefined
}): number | false {
  if (!args.live) return false
  return hasAnyActiveGeneration(args.reports)
    ? LIVE_SEO_REPORTS_REFETCH_INTERVAL_MS
    : false
}

export function getSeoReportsQueryRefetchInterval(args: {
  live?: boolean
  status: string
  reports: SeoReportListItem[] | undefined
}): number | false {
  if (args.live && args.status === 'error' && !args.reports) {
    return LIVE_SEO_REPORTS_REFETCH_INTERVAL_MS
  }

  return getSeoReportsRefetchInterval({
    live: args.live,
    reports: args.reports,
  })
}

export function getSeoReportDetailRefetchInterval(args: {
  live?: boolean
  status: string
  report: PublishedResearchReport | null | undefined
}): number | false {
  if (!args.live) return false
  if (args.status === 'error' && args.report === undefined) {
    return LIVE_SEO_REPORT_DETAIL_REFETCH_INTERVAL_MS
  }

  return hasActiveGeneration(args.report)
    ? LIVE_SEO_REPORT_DETAIL_REFETCH_INTERVAL_MS
    : false
}

async function fetchSeoReportsPage(input: {
  authUser?: AuthUserWithToken | null | undefined
  status?: ResearchReportStatus
  limit: number
  offset: number
  includeTotal: boolean
}): Promise<SeoReportsListResponse> {
  return fetcher<SeoReportsListResponse>(
    `${DUPE_API_URL}/api/admin/seo-reports`,
    {
      method: 'GET',
      authUser: input.authUser,
      dataOrParams: {
        ...(input.status ? { status: input.status } : {}),
        limit: input.limit,
        offset: input.offset,
        include_total: input.includeTotal,
      },
    }
  )
}

export async function fetchSeoReportsForAdmin(input: {
  authUser?: AuthUserWithToken | null | undefined
  status?: ResearchReportStatus
}): Promise<SeoReportsListResponse['data']> {
  const firstPage = await fetchSeoReportsPage({
    authUser: input.authUser,
    status: input.status,
    limit: SEO_REPORTS_ADMIN_PAGE_SIZE,
    offset: 0,
    includeTotal: true,
  })
  const allReports: SeoReportsListResponse['data'] = [...firstPage.data]
  const maxReports = SEO_REPORTS_ADMIN_PAGE_SIZE * SEO_REPORTS_ADMIN_MAX_PAGES
  const expectedTotal = Math.min(
    firstPage.total ?? firstPage.data.length,
    maxReports
  )
  const remainingOffsets: number[] = []

  for (
    let offset = SEO_REPORTS_ADMIN_PAGE_SIZE;
    offset < expectedTotal;
    offset += SEO_REPORTS_ADMIN_PAGE_SIZE
  ) {
    remainingOffsets.push(offset)
  }

  const remainingPages = await Promise.all(
    remainingOffsets.map((offset) =>
      fetchSeoReportsPage({
        authUser: input.authUser,
        status: input.status,
        limit: SEO_REPORTS_ADMIN_PAGE_SIZE,
        offset,
        includeTotal: false,
      })
    )
  )

  for (const page of remainingPages) {
    allReports.push(...page.data)
  }

  if (firstPage.total !== null && firstPage.total > maxReports) {
    console.warn(
      '[seo-reports] Reached max dashboard page fetch limit; results may be truncated.',
      {
        fetched: allReports.length,
        maxPages: SEO_REPORTS_ADMIN_MAX_PAGES,
        pageSize: SEO_REPORTS_ADMIN_PAGE_SIZE,
        total: firstPage.total,
      }
    )
  }

  return allReports
}

export function useSeoReports(
  params?: { status?: ResearchReportStatus },
  options?: { live?: boolean; enabled?: boolean }
) {
  const { authUser, isAdmin } = useAuthUser()

  return useQuery<SeoReportsListResponse['data']>({
    queryKey: ['admin-seo-reports', params?.status ?? 'all'],
    enabled: Boolean(authUser && isAdmin && (options?.enabled ?? true)),
    retry: false,
    refetchOnWindowFocus: Boolean(options?.live),
    refetchInterval: (query) =>
      getSeoReportsQueryRefetchInterval({
        live: options?.live,
        status: query.state.status,
        reports: query.state.data,
      }),
    refetchIntervalInBackground: false,
    queryFn: () =>
      fetchSeoReportsForAdmin({
        authUser,
        status: params?.status,
      }),
  })
}

export function useEligibleSeoTasks(
  params?: {
    limit?: number
    offset?: number
    search?: string
    includeImages?: boolean
    includeTotal?: boolean
  },
  options?: { enabled?: boolean }
) {
  const { authUser, isAdmin } = useAuthUser()
  const limit = params?.limit ?? 25
  const offset = params?.offset ?? 0
  const includeImages = params?.includeImages ?? true
  const includeTotal = params?.includeTotal ?? true

  return useQuery<{
    data: EligibleTask[]
    total: number | null
    hasMore: boolean
  }>({
    queryKey: [
      'admin-seo-reports-eligible-tasks',
      limit,
      offset,
      params?.search ?? '',
      includeImages,
      includeTotal,
    ],
    enabled: Boolean(authUser && isAdmin && (options?.enabled ?? true)),
    placeholderData: (previousData) => previousData,
    queryFn: async () => {
      const response = await fetcher<EligibleTasksResponse>(
        `${DUPE_API_URL}/api/admin/seo-reports/eligible-tasks`,
        {
          method: 'GET',
          authUser,
          dataOrParams: {
            limit,
            offset,
            include_images: includeImages,
            include_total: includeTotal,
            ...(params?.search ? { search: params.search } : {}),
          },
        }
      )

      return {
        data: response.data,
        total: response.total,
        hasMore: response.hasMore,
      }
    },
  })
}

export function useSeoReportByTask(
  taskId?: string,
  options?: {
    live?: boolean
  }
) {
  const { authUser, isAdmin } = useAuthUser()

  return useQuery<PublishedResearchReport | null>({
    queryKey: ['admin-seo-report-task', taskId],
    enabled: Boolean(authUser && isAdmin && taskId),
    retry: false,
    refetchOnWindowFocus: Boolean(options?.live),
    refetchInterval: (query) =>
      getSeoReportDetailRefetchInterval({
        live: options?.live,
        status: query.state.status,
        report: query.state.data,
      }),
    queryFn: async () => {
      const response = await fetcher<SeoReportByTaskResponse>(
        `${DUPE_API_URL}/api/admin/seo-reports/task/${encodeURIComponent(taskId!)}`,
        {
          method: 'GET',
          authUser,
        }
      )

      return response.data
    },
  })
}

export function useSeoReportById(
  reportId?: string,
  options?: {
    live?: boolean
  }
) {
  const { authUser, isAdmin } = useAuthUser()

  return useQuery<PublishedResearchReport>({
    queryKey: ['admin-seo-report-id', reportId],
    enabled: Boolean(authUser && isAdmin && reportId),
    retry: false,
    refetchOnWindowFocus: Boolean(options?.live),
    refetchInterval: (query) =>
      getSeoReportDetailRefetchInterval({
        live: options?.live,
        status: query.state.status,
        report: query.state.data,
      }),
    queryFn: async () => {
      const response = await fetcher<SeoReportByIdResponse>(
        `${DUPE_API_URL}/api/admin/seo-reports/${encodeURIComponent(reportId!)}`,
        {
          method: 'GET',
          authUser,
        }
      )

      return response.data
    },
  })
}

export function useSeoReportActions() {
  const { authUser } = useAuthUser()
  const queryClient = useQueryClient()

  const invalidateSeoQueries = async (taskId?: string | null) => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: ['admin-seo-reports'],
        exact: false,
      }),
      queryClient.invalidateQueries({
        queryKey: ['admin-seo-reports-eligible-tasks'],
        exact: false,
      }),
      queryClient.invalidateQueries({
        queryKey: ['admin-seo-report-task'],
        exact: false,
      }),
      queryClient.invalidateQueries({
        queryKey: ['admin-seo-report-id'],
        exact: false,
      }),
      ...(taskId
        ? [
            queryClient.invalidateQueries({
              queryKey: ['admin-seo-report-task', taskId],
            }),
          ]
        : []),
    ])
  }
  const optimisticallyQueueReports = async (
    reportIds: readonly string[],
    patch: SeoReportGenerationCachePatch
  ): Promise<SeoReportCacheMutationContext> => {
    await cancelSeoReportCacheQueries(queryClient)
    const snapshot = getSeoReportCacheSnapshot(queryClient)
    patchSeoReportCache(queryClient, reportIds, (report) =>
      queueSeoReportGenerationForAdminCache(report, patch)
    )
    return { snapshot }
  }
  const restoreOptimisticQueue = (
    context: SeoReportCacheMutationContext | undefined
  ) => {
    restoreSeoReportCacheSnapshot(queryClient, context?.snapshot)
  }

  const createReport = useMutation({
    mutationFn: async (input: {
      taskId: string
      slug?: string
      keyword: string
      categorySlug?: string
    }) => {
      return fetcher<CreateSeoReportResponse>(
        `${DUPE_API_URL}/api/admin/seo-reports`,
        {
          method: 'POST',
          authUser,
          dataOrParams: input,
        }
      )
    },
    onSuccess: async (response) => {
      await invalidateSeoQueries(response.data.sourceTaskId ?? null)
    },
  })

  const publishReport = useMutation({
    mutationFn: async (reportId: string) => {
      return fetcher<{ message: string }>(
        `${DUPE_API_URL}/api/admin/seo-reports/${reportId}/publish`,
        {
          method: 'POST',
          authUser,
        }
      )
    },
    onMutate: (reportId) =>
      optimisticallyQueueReports([reportId], { reportProcessing: true }),
    onError: (_error, _reportId, context) => {
      restoreOptimisticQueue(context)
    },
    onSettled: async () => {
      await invalidateSeoQueries()
    },
  })

  const unpublishReport = useMutation({
    mutationFn: async (reportId: string) => {
      return fetcher<{ message: string }>(
        `${DUPE_API_URL}/api/admin/seo-reports/${reportId}/unpublish`,
        {
          method: 'POST',
          authUser,
        }
      )
    },
    onSuccess: async () => {
      await invalidateSeoQueries()
    },
  })

  const deleteReport = useMutation({
    mutationFn: async (reportId: string) => {
      return fetcher<{ message: string }>(
        `${DUPE_API_URL}/api/admin/seo-reports/${reportId}`,
        {
          method: 'DELETE',
          authUser,
        }
      )
    },
    onSuccess: async () => {
      await invalidateSeoQueries()
    },
  })

  const regenerateReport = useMutation({
    mutationFn: async (reportId: string) => {
      return fetcher<{ message: string }>(
        `${DUPE_API_URL}/api/admin/seo-reports/${reportId}/regenerate`,
        {
          method: 'POST',
          authUser,
        }
      )
    },
    onMutate: (reportId) =>
      optimisticallyQueueReports([reportId], { reportProcessing: true }),
    onError: (_error, _reportId, context) => {
      restoreOptimisticQueue(context)
    },
    onSettled: async () => {
      await invalidateSeoQueries()
    },
  })

  const regenerateSlug = useMutation({
    mutationFn: async (reportId: string) => {
      try {
        return await fetcher<RegenerateSeoReportSlugResponse>(
          `${DUPE_API_URL}/api/admin/seo-reports/${reportId}/regenerate-slug`,
          {
            method: 'POST',
            authUser,
          }
        )
      } catch (error) {
        if (
          error instanceof Error &&
          'status' in error &&
          error.status === 404
        ) {
          // TODO(2026-05-14): remove this PUT fallback after all API workers
          // have POST /regenerate-slug deployed.
          const fallbackResponse =
            await fetcher<RegenerateSeoReportSlugResponse>(
              `${DUPE_API_URL}/api/admin/seo-reports/${reportId}`,
              {
                method: 'PUT',
                authUser,
                dataOrParams: { regenerateSlug: true },
              }
            )
          if (!fallbackResponse.slugSource) {
            throw new Error(
              'The API server has not loaded slug regeneration yet. Restart the API dev server and try again.'
            )
          }
          return fallbackResponse
        }
        throw error
      }
    },
    onSuccess: async (response) => {
      await invalidateSeoQueries(response.data.sourceTaskId ?? null)
    },
  })

  const cancelGeneration = useMutation({
    mutationFn: async (input: {
      reportId: string
      target?: SeoAdminComponentTarget
    }) => {
      return fetcher<{ message: string }>(
        `${DUPE_API_URL}/api/admin/seo-reports/${input.reportId}/cancel-generation`,
        {
          method: 'POST',
          authUser,
          dataOrParams: input.target ? { target: input.target } : {},
        }
      )
    },
    onSuccess: async () => {
      await invalidateSeoQueries()
    },
  })

  const generateComponents = useMutation({
    mutationFn: async (input: {
      reportId: string
      targets: SeoAdminComponentTarget[]
      force?: boolean
    }) => {
      return fetcher<{ message: string }>(
        `${DUPE_API_URL}/api/admin/seo-reports/${input.reportId}/generate-components`,
        {
          method: 'POST',
          authUser,
          dataOrParams: {
            targets: input.targets,
            force: input.force ?? false,
          },
        }
      )
    },
    onMutate: (input) =>
      optimisticallyQueueReports([input.reportId], {
        componentTargets: input.targets,
      }),
    onError: (_error, _input, context) => {
      restoreOptimisticQueue(context)
    },
    onSettled: async () => {
      await invalidateSeoQueries()
    },
  })

  const generateSeoMetadata = useMutation({
    mutationFn: async (input: { reportId: string; force?: boolean }) => {
      return fetcher<{ message: string }>(
        `${DUPE_API_URL}/api/admin/seo-reports/${input.reportId}/generate-seo-metadata`,
        {
          method: 'POST',
          authUser,
          dataOrParams: {
            force: input.force ?? false,
          },
        }
      )
    },
    onMutate: (input) =>
      optimisticallyQueueReports([input.reportId], { seoMetadata: true }),
    onError: (_error, _input, context) => {
      restoreOptimisticQueue(context)
    },
    onSettled: async () => {
      await invalidateSeoQueries()
    },
  })

  const regenerateProductImages = useMutation({
    mutationFn: async (input: { reportId: string; force?: boolean }) => {
      return fetcher<SeoReportProductImagesQueueResponse>(
        `${DUPE_API_URL}/api/admin/seo-reports/${input.reportId}/regenerate-product-images`,
        {
          method: 'POST',
          authUser,
          dataOrParams: {
            force: input.force ?? true,
          },
        }
      )
    },
    onMutate: (input) =>
      optimisticallyQueueReports([input.reportId], { productImages: true }),
    onError: (_error, _input, context) => {
      restoreOptimisticQueue(context)
    },
    onSettled: async () => {
      await invalidateSeoQueries()
    },
  })

  const backfillProductImages = useMutation({
    mutationFn: async (input?: {
      ids?: string[]
      limit?: number
      offset?: number
      force?: boolean
    }) => {
      return fetcher<SeoReportProductImagesQueueResponse>(
        `${DUPE_API_URL}/api/admin/seo-reports/backfill-product-images`,
        {
          method: 'POST',
          authUser,
          dataOrParams: {
            ids: input?.ids,
            limit: input?.limit ?? 10,
            offset: input?.offset ?? 0,
            force: input?.force ?? false,
          },
        }
      )
    },
    onMutate: (input) =>
      optimisticallyQueueReports(input?.ids ?? [], { productImages: true }),
    onSuccess: async (response) => {
      const queuedReportIds =
        response.data.results
          ?.filter((result) => result.status === 'queued')
          .map((result) => result.reportId) ?? []
      patchSeoReportCache(queryClient, queuedReportIds, (report) =>
        queueSeoReportGenerationForAdminCache(report, {
          productImages: true,
        })
      )
    },
    onError: (_error, _input, context) => {
      restoreOptimisticQueue(context)
    },
    onSettled: async () => {
      await invalidateSeoQueries()
    },
  })

  const publishTaskForSeo = useMutation({
    mutationFn: async (input: { taskId: string; title: string }) => {
      return publishTaskForSeoRequest({
        ...input,
        authUser,
        onReportReadyForPublish: (report) => {
          upsertSeoReportCache(
            queryClient,
            queueSeoReportGenerationForAdminCache(report, {
              reportProcessing: true,
            })
          )
        },
        onCreatedReportRollback: (report) => {
          removeSeoReportFromAdminCache(queryClient, report)
        },
      })
    },
    onSuccess: (data) => {
      patchSeoReportCache(queryClient, [data.reportId], (report) =>
        queueSeoReportGenerationForAdminCache(report, {
          reportProcessing: true,
        })
      )
    },
    onSettled: async (_data, _error, variables) => {
      await invalidateSeoQueries(variables.taskId)
    },
  })

  return {
    createReport,
    publishReport,
    unpublishReport,
    deleteReport,
    regenerateReport,
    regenerateSlug,
    cancelGeneration,
    generateComponents,
    generateSeoMetadata,
    regenerateProductImages,
    backfillProductImages,
    publishTaskForSeo,
  }
}
